import { useLanguage } from "@/i18n/LanguageContext";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";

const tiers = [
  {
    tag: "Small Workshop / Garage Factory",
    title: "Start with visibility and control.",
    text: "Use BEEDIGIT to monitor key machines, track output, understand downtime, issue digital job orders and reduce manual reporting without building an IT department.",
    modules: ["Machine Monitoring", "OEE", "Job Orders", "QR Labels", "Basic Quality"],
  },
  {
    tag: "Growing Manufacturer",
    title: "Standardize production execution.",
    text: "Connect multiple lines, manage job orders, material flow, QR traceability, quality checks, maintenance requests and energy usage in one platform.",
    modules: ["MES", "Material Flow", "QR Coding", "Quality", "CMMS", "Energy"],
  },
  {
    tag: "Manufacturing Holding",
    title: "Scale across factories and integrate with enterprise systems.",
    text: "Use BEEDIGIT as a standard shop-floor execution layer across plants, integrated with ERP, APS, BI and group-level reporting.",
    modules: ["Multi-site MES", "Advanced OEE", "Integrations", "Energy ISO 50001", "Enterprise Dashboards", "Role-Based Access"],
  },
];

export default function StartSmallScaleBig() {
  const { t } = useLanguage();
  return (
    <section className="py-16 md:py-20 border-t border-border bg-[hsl(var(--section-alt))]">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mb-10">
          <p className="eyebrow mb-3">{t("Start small · scale big")}</p>
          <h2 className="text-3xl md:text-4xl font-bold mb-3">{t("From one machine to a multi-site manufacturing holding.")}</h2>
          <p className="text-muted-foreground">
            {t("You do not need to start with a massive MES project. Start where losses are visible: one bottleneck, one line, one work center, one factory. Then scale when the business case is proven.")}
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {tiers.map((tier) => (
            <div key={tier.tag} className="rounded-lg border border-border bg-card p-5 flex flex-col">
              <p className="eyebrow mb-3">{t(tier.tag)}</p>
              <h3 className="font-semibold text-lg mb-2">{t(tier.title)}</h3>
              <p className="text-sm text-muted-foreground mb-4 flex-1">{t(tier.text)}</p>
              <div>
                <p className="text-xs text-muted-foreground mb-2">{t("Best modules:")}</p>
                <div className="flex flex-wrap gap-1.5">
                  {tier.modules.map((m) => (
                    <span key={m} className="text-[11px] rounded-full border border-border bg-background/50 px-2 py-0.5">{t(m)}</span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-8">
          <Button asChild>
            <Link to="/pricing">{t("Find Your Starting Point")} <ArrowRight className="ms-2 h-4 w-4" /></Link>
          </Button>
        </div>
      </div>
    </section>
  );
}